import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';

import { useDailySeed } from '../../hooks/useDailySeed';
import { useGuessHistory } from '../../hooks/useGuessHistory';
import { useRoundEmojis } from '../../hooks/useRoundEmojis';
import { useTodaysCountry } from '../../providers/TodaysCountryProvider';
import {
  buildResultHeadline,
  buildShareBody,
  shareSquaresAsTiles,
} from '../../utils/dailyResult';
import { averageGuesses } from '../../utils/dailyScore';
import { formatPuzzleDate } from '../../utils/helpers';
import { defaultStats, Stats } from '../../utils/stats';
import EmojiRender from '../EmojiRender';
import { ShareScoreIcons } from './ShareScoreIcons';

/**
 * The little paper flecks around the heading. Purely decorative, so they are
 * hidden from assistive tech.
 */
const CONFETTI = [
  '-left-1 top-1 -rotate-12 bg-[#3B82F6]',
  'left-3 -bottom-1 rotate-45 bg-[#FACC15]',
  '-right-2 -top-1 rotate-12 bg-[#22C55E]',
  'right-0 bottom-0 -rotate-45 bg-[#EF4444]',
];

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col flex-1 gap-1 items-center justify-center">
    <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400">
      {label}
    </span>
    <span className="text-2xl font-bold leading-none">{value}</span>
  </div>
);

/**
 * The headline block on the daily end screen: how it went, the shareable
 * result line and squares, and two stats.
 *
 * Self-contained on purpose — it reads what it needs from the existing daily
 * providers, so it can be dropped into or removed from RoundEndedButtons in
 * one line without touching anything else.
 */
export const DailyEndSummary = () => {
  const { t, i18n } = useTranslation();
  const { isArchive, dayString } = useTodaysCountry();
  const dailySeed = useDailySeed(isArchive);
  const [guessHistory] = useGuessHistory(isArchive);
  const [stats] = useLocalStorage<Stats>('stats', defaultStats);
  const roundEmojis = useRoundEmojis(isArchive);

  const guesses = guessHistory[dailySeed] ?? [];
  const won = guesses.some(({ distance }) => distance === 0);
  const score = won ? guesses.length : 'DNF';
  const result = { score, guesses, dayString } as const;

  const average = averageGuesses(stats);

  return (
    <div className="flex flex-col w-full gap-4 items-center text-center">
      <div className="flex flex-col gap-1 items-center">
        {/* EmojiRender draws the flag as a Twemoji SVG, so it does not depend
            on the device having an emoji font. */}
        <div className="relative flex px-6 gap-2 items-center justify-center">
          {CONFETTI.map((position) => (
            <span
              key={position}
              aria-hidden="true"
              className={`absolute w-1.5 h-3 rounded-[2px] ${position}`}
            />
          ))}
          <span className="text-3xl font-bold">{t('daily-end.well-done')}</span>
          <EmojiRender
            text="🏳️"
            className="inline-flex items-center [&_img]:w-8 [&_img]:h-8"
          />
        </div>
        <p className="text-sm text-gray-500 dark:text-slate-400">
          {t('daily-end.finished')}
        </p>
        <p className="flex gap-1.5 items-center text-sm text-gray-500 dark:text-slate-400">
          <CalendarMonthRoundedIcon style={{ fontSize: 16 }} />
          {formatPuzzleDate(dayString, i18n.language)}
        </p>
      </div>

      <div className="flex flex-col gap-2 items-center">
        {/* The share text leads with a hashtag; on screen that reads as noise. */}
        <p className="text-base font-bold">
          {buildResultHeadline(result).replace(/^#/, '')}
        </p>
        {/* Three across, two down: the same arrangement as the flag's own
            tiles, and as the squares in the shared text. */}
        <div
          id="daily-squares"
          className="grid w-fit mx-auto grid-cols-3 gap-1.5"
        >
          {shareSquaresAsTiles(result).map((square, index) => (
            <span
              key={index}
              className={`w-10 h-10 rounded-lg ${
                square === '🟩' ? 'bg-[#289535]' : 'bg-[#CC0A1C]'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="flex w-full py-4 rounded-xl border border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-800/60">
        <Stat
          label={t('daily-end.avg-guesses')}
          value={average === null ? '—' : `${average}`}
        />
        <div className="w-px bg-gray-200 dark:bg-slate-700" />
        {/* Placeholder until there is a data source for it: ranking a player
            needs the day's distribution across everyone, which nothing in the
            CDN or the accounts API exposes today. It shows a dash rather than
            a number so it can never be mistaken for a real figure. */}
        <Stat label={t('daily-end.percentile')} value="—" />
      </div>

      <div className="flex flex-col w-full py-4 gap-3 items-center rounded-xl border border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-800/60">
        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400">
          {t('daily-end.share-score')}
        </span>
        <ShareScoreIcons body={buildShareBody(result, roundEmojis)} />
      </div>
    </div>
  );
};
